import {
  Box,
  FormControlLabel,
  IconButton,
  Radio,
  SelectChangeEvent,
  TextField,
  Tooltip,
} from "@mui/material";
import DropdownSelect from "../../../../common/DropdownSelect";
import { ChangeEvent } from "react";
import { getIn, useFormikContext } from "formik";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { addDays, endOfDay, startOfDay } from "date-fns";
import { AddSolutionFormValues } from "../../../add/form/types";
import { EditSolutionFormValues } from "../../../edit/form/types";
import { baseOutcomeOptions } from "../../types";
import type {
  CounterAwareFieldError,
  OutcomeInputProps,
  OutcomeType,
} from "../../types";

const coBenefitsOutcomeOptions = ["Hours of Education", "Hours of Employment"];

const AllOutcomeOptions = [...baseOutcomeOptions, ...coBenefitsOutcomeOptions];

const hasTimestamp = (
  timestamp: number | null | undefined,
): timestamp is number => timestamp !== null && timestamp !== undefined;

const isValidTimestamp = (
  timestamp: number | null | undefined,
): timestamp is number => hasTimestamp(timestamp) && Number.isFinite(timestamp);

function Outcome({ value, index, remove }: OutcomeInputProps) {
  const context = useFormikContext<
    AddSolutionFormValues | EditSolutionFormValues
  >();
  const isSavedOutcome = value.origin === "saved";

  const closePreviousMatchingUnitOutcome = (
    outcomes: Array<OutcomeType>,
    currentOutcomeCounter: number,
    currentUnit: string,
    currentStartDate: number,
  ) => {
    const previousMatchingUnitOutcome = [...outcomes]
      .filter(
        (outcome) =>
          outcome.outcomeCounter !== currentOutcomeCounter &&
          outcome.unit === currentUnit &&
          isValidTimestamp(outcome["outcome-start-date"]) &&
          Number(outcome["outcome-start-date"]) < currentStartDate,
      )
      .sort((a, b) =>
        Number(a["outcome-start-date"]) === Number(b["outcome-start-date"])
          ? b.outcomeCounter - a.outcomeCounter
          : Number(b["outcome-start-date"]) - Number(a["outcome-start-date"]),
      )[0];

    if (!previousMatchingUnitOutcome) {
      return outcomes;
    }

    const previousOutcomeEndDate = endOfDay(
      addDays(new Date(currentStartDate), -1),
    ).getTime();

    return outcomes.map((outcome) =>
      outcome.outcomeCounter === previousMatchingUnitOutcome.outcomeCounter
        ? {
            ...outcome,
            "outcome-end-date": previousOutcomeEndDate,
          }
        : outcome,
    );
  };

  const getDateFromTimestamp = (timestamp: number | null | undefined) => {
    return isValidTimestamp(timestamp) ? new Date(timestamp) : null;
  };

  const setFieldValueAsFormState = (
    fieldName: string,
    fieldValue: string | number | boolean | null,
  ) => {
    let updatedOutcomes = context.values.outcomes.map((outcome) => {
      if (outcome.outcomeCounter !== value.outcomeCounter) {
        return outcome;
      }

      return {
        ...outcome,
        [fieldName]: fieldValue,
      };
    });

    const currentOutcome = updatedOutcomes.find(
      (outcome) => outcome.outcomeCounter === value.outcomeCounter,
    );

    if (
      (fieldName === "outcome-start-date" || fieldName === "unit") &&
      currentOutcome?.unit &&
      isValidTimestamp(currentOutcome["outcome-start-date"])
    ) {
      updatedOutcomes = closePreviousMatchingUnitOutcome(
        updatedOutcomes,
        value.outcomeCounter,
        currentOutcome.unit,
        currentOutcome["outcome-start-date"],
      );
    }

    context.setFieldValue("outcomes", updatedOutcomes, false);
  };

  const setValueAsFormState = (
    event:
      | SelectChangeEvent<string | number | string[]>
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldName: string,
  ) => {
    setFieldValueAsFormState(
      fieldName,
      (event.target as HTMLInputElement).value,
    );
  };

  const setDateAsFormState = (value: Date | null, fieldName: string) => {
    if (!value) {
      setFieldValueAsFormState(fieldName, null);
      return;
    }

    const normalisedDate =
      fieldName === "outcome-start-date"
        ? startOfDay(new Date(value)).getTime()
        : endOfDay(new Date(value)).getTime();

    setFieldValueAsFormState(fieldName, normalisedDate);
  };

  const setUnitOfSaleFormState = () => {
    let updatedOutcomes = context.values.outcomes.map((outcome) =>
      outcome.outcomeCounter === value.outcomeCounter
        ? {
            ...outcome,
            "unit-of-sale": !outcome["unit-of-sale"],
          }
        : outcome,
    );

    const currentOutcome = updatedOutcomes.find(
      (outcome) => outcome.outcomeCounter === value.outcomeCounter,
    );

    if (
      currentOutcome?.["unit-of-sale"] &&
      currentOutcome?.unit &&
      isValidTimestamp(currentOutcome["outcome-start-date"])
    ) {
      updatedOutcomes = closePreviousMatchingUnitOutcome(
        updatedOutcomes,
        value.outcomeCounter,
        currentOutcome.unit,
        currentOutcome["outcome-start-date"],
      );
    }

    context.setFieldValue("outcomes", updatedOutcomes);
  };

  const getValueFromFormState = (fieldName: string) => {
    const outcome = context.values.outcomes.find(
      (outcome) => outcome.outcomeCounter === value.outcomeCounter,
    );

    return outcome ? outcome[fieldName as keyof OutcomeType] : "";
  };

  const getFieldError = (fieldName: string) => {
    const fieldError = getIn(
      context.errors,
      `outcomes[${index}].${fieldName}`,
    ) as CounterAwareFieldError | undefined;

    if (!fieldError) return undefined;
    if (typeof fieldError === "string") return fieldError;

    return fieldError.message;
  };

  return (
    <Box
      data-testid={`solution-outcome-container-${value.outcomeCounter}`}
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box
        data-testid={`solution-outcome-container-input-fields-${value.outcomeCounter}`}
        sx={{
          width: "95%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "start",
          paddingBottom: 2,
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box
            data-testid={`solution-outcome-input-row-${value.outcomeCounter}`}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 2,
              "@media (max-width:1000px)": {
                flexDirection: "column",
                alignItems: "stretch",
              },
            }}
          >
            <Box
              data-testid={`solution-outcome-unit-value-row-${value.outcomeCounter}`}
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 2,
                flex: "0 0 38%",
                "@media (max-width:1000px)": {
                  flex: "1 1 auto",
                },
              }}
            >
              <Box
                sx={{
                  flex: "1 1 0",
                }}
              >
                <DropdownSelect
                  id={`solution-outcome-unit-${value.outcomeCounter}`}
                  options={AllOutcomeOptions.sort((a, b) =>
                    a.toLowerCase().localeCompare(b.toLowerCase()),
                  )}
                  value={getValueFromFormState("unit") as string}
                  onChange={(event) => setValueAsFormState(event, "unit")}
                  label="Unit"
                  error={Boolean(getFieldError("unit"))}
                  helperText={getFieldError("unit")}
                />
              </Box>
              <TextField
                id={`solution-outcome-value-${value.outcomeCounter}`}
                variant="standard"
                label="Value"
                type="number"
                inputProps={{
                  min: 0,
                  step: "any",
                  inputMode: "decimal",
                }}
                sx={{
                  flex: "0 0 28%",
                  minWidth: {
                    xs: 0,
                    md: 92,
                  },
                }}
                onChange={(event) => setValueAsFormState(event, "value")}
                onWheel={(event) => (event.target as HTMLInputElement).blur()}
                value={getValueFromFormState("value")}
                error={Boolean(getFieldError("value"))}
                helperText={getFieldError("value")}
              />
            </Box>
            <Box
              data-testid={`solution-outcome-date-row-${value.outcomeCounter}`}
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 2,
                flex: "1 1 0",
                "@media (max-width:1000px)": {
                  flex: "1 1 auto",
                },
              }}
            >
              <Box
                sx={{
                  flex: "1 1 0",
                }}
              >
                <DesktopDatePicker
                  label="Start date"
                  value={
                    getDateFromTimestamp(
                      getValueFromFormState("outcome-start-date") as
                        number | null | undefined,
                    ) as Date | null
                  }
                  format="dd/MM/yyyy"
                  onChange={(date) =>
                    setDateAsFormState(date, "outcome-start-date")
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      id: `solution-outcome-start-date-${value.outcomeCounter}`,
                      name: `solution-outcome-start-date-${value.outcomeCounter}`,
                      error: Boolean(getFieldError("outcome-start-date")),
                      helperText: getFieldError("outcome-start-date") || " ",
                      variant: "standard",
                      inputProps: {
                        "data-testid": `solution-outcome-start-date-${value.outcomeCounter}`,
                      },
                    },
                  }}
                />
              </Box>
              <Box
                sx={{
                  flex: "1 1 0",
                }}
              >
                <DesktopDatePicker
                  label="End date"
                  value={
                    getDateFromTimestamp(
                      getValueFromFormState("outcome-end-date") as
                        number | null | undefined,
                    ) as Date | null
                  }
                  format="dd/MM/yyyy"
                  onChange={(date) =>
                    setDateAsFormState(date, "outcome-end-date")
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      id: `solution-outcome-end-date-${value.outcomeCounter}`,
                      name: `solution-outcome-end-date-${value.outcomeCounter}`,
                      error: Boolean(getFieldError("outcome-end-date")),
                      helperText: getFieldError("outcome-end-date") || " ",
                      variant: "standard",
                      inputProps: {
                        "data-testid": `solution-outcome-end-date-${value.outcomeCounter}`,
                      },
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </LocalizationProvider>
      </Box>
      <Box
        data-testid={`solution-outcome-actions-${value.outcomeCounter}`}
        sx={{
          width: "95%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <FormControlLabel
          sx={{
            marginRight: 0,
          }}
          control={
            <Radio
              id={`solution-outcome-unit-of-sale-${value.outcomeCounter}`}
              checked={getValueFromFormState("unit-of-sale") as boolean}
              onClick={() => setUnitOfSaleFormState()}
              name={`unit-of-sale-radio-${value.outcomeCounter}`}
            />
          }
          label="Unit of Sale"
        />{" "}
        <Tooltip
          title={
            isSavedOutcome
              ? "Saved outcomes cannot be deleted"
              : "Delete outcome"
          }
        >
          <span>
            <IconButton
              onClick={remove}
              disabled={isSavedOutcome}
              sx={{
                padding: 0,
              }}
              aria-label={`delete-outcome-${value.outcomeCounter}`}
              test-id={`delete-outcome-${value.outcomeCounter}`}
              size="large"
            >
              <DeleteForeverIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default Outcome;
